/**
 * Pre-React boot guard.
 *
 * The React ErrorBoundary cannot catch crashes that happen BEFORE the root
 * mounts — for example a TDZ error or a failed chunk download in a vendor
 * bundle (e.g. "Cannot access 'H' before initialization", "Failed to fetch
 * dynamically imported module", "Loading chunk N failed"). Those errors leave
 * the user staring at the static skeleton with no way to recover.
 *
 * This guard listens for `error` and `unhandledrejection` at the window level
 * BEFORE any chunks finish evaluating. When it detects a chunk-load / TDZ
 * failure it:
 *   1. Reports to Sentry (if loaded).
 *   2. Renders a friendly fallback into #root with a "Tentar novamente" button
 *      that does a hard reload (busting the cached broken chunk).
 *   3. Auto-reloads once after a short delay if the broken chunk looks like a
 *      stale-deploy mismatch (only one auto-reload per session, tracked via
 *      sessionStorage, to avoid reload loops).
 */

const RELOAD_KEY = "__cbbr_boot_reload";

const CHUNK_LOAD_PATTERNS: RegExp[] = [
  /Loading chunk \S+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
  /ChunkLoadError/i,
  /Cannot access '\S+' before initialization/i, // TDZ from circular chunk order
];

function isChunkOrTdzError(err: unknown): boolean {
  const msg =
    err instanceof Error
      ? `${err.name}: ${err.message}`
      : typeof err === "string"
        ? err
        : "";
  return CHUNK_LOAD_PATTERNS.some((re) => re.test(msg));
}

function renderFallback(message: string) {
  const root = document.getElementById("root");
  if (!root) return;
  root.innerHTML = `
    <div role="alert" aria-live="assertive" style="min-height:100vh;display:flex;align-items:center;justify-content:center;padding:24px;background:#0F172A;color:#fff;font-family:'DM Sans',ui-sans-serif,system-ui,sans-serif;">
      <div style="max-width:480px;text-align:center;">
        <div style="width:64px;height:64px;border-radius:50%;background:rgba(252,211,77,.15);display:flex;align-items:center;justify-content:center;margin:0 auto 20px;font-size:32px;">⚠️</div>
        <h1 style="font-family:'Space Grotesk',ui-sans-serif,sans-serif;font-size:24px;font-weight:700;margin:0 0 12px;">Não conseguimos carregar o app</h1>
        <p style="font-size:15px;opacity:.8;margin:0 0 24px;line-height:1.5;">${message}</p>
        <button id="cbbr-retry" style="background:#10B981;color:#fff;border:0;border-radius:12px;padding:14px 28px;font-weight:700;font-size:16px;cursor:pointer;">Tentar novamente</button>
      </div>
    </div>`;
  document
    .getElementById("cbbr-retry")
    ?.addEventListener("click", () => hardReload());
}

function hardReload() {
  try {
    // Bust any HTTP cache so we redownload fresh chunks.
    const url = new URL(window.location.href);
    url.searchParams.set("__r", Date.now().toString(36));
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
}

function reportToSentry(err: unknown) {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any;
    w.Sentry?.captureException?.(err, {
      tags: { phase: "boot", subsystem: "chunk-loader" },
    });
  } catch {
    /* noop */
  }
}

let handled = false;

function handle(err: unknown) {
  if (handled || !isChunkOrTdzError(err)) return;
  handled = true;
  reportToSentry(err);

  const alreadyReloaded = sessionStorage.getItem(RELOAD_KEY) === "1";
  if (!alreadyReloaded) {
    sessionStorage.setItem(RELOAD_KEY, "1");
    // Likely a stale-deploy mismatch — one silent retry.
    setTimeout(hardReload, 250);
    return;
  }

  renderFallback(
    "Sua conexão pode estar instável ou houve uma atualização recente. Recarregue para baixar a versão mais recente.",
  );
}

export function installBootGuard(): void {
  window.addEventListener("error", (e) => {
    // SCRIPT load failures surface as ErrorEvent with no `.error` but useful target
    if (
      !e.error &&
      e.target &&
      (e.target as HTMLElement).tagName === "SCRIPT"
    ) {
      handle(
        new Error(`Script load failed: ${(e.target as HTMLScriptElement).src}`),
      );
      return;
    }
    handle(e.error ?? e.message);
  });

  window.addEventListener("unhandledrejection", (e) => {
    handle(e.reason);
  });

  // Clear the reload flag once React successfully mounts (good boot).
  // App.tsx imports nothing from here — main.tsx calls markBootSuccess
  // after createRoot/render completes.
}

export function markBootSuccess(): void {
  try {
    sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    /* noop */
  }
}
